import { Controller } from "stimulus"

// Reusable accessible modal dialog built on the native <dialog> element, which
// supplies the focus trap, Escape handling, and top-layer backdrop natively.
// This controller adds focus restoration, a "locked" mode that refuses to close
// while an operation must not be interrupted, and composition events.
//
// Usage:
//   <div data-controller="dialog">
//     <button type="button" data-action="dialog#open">Open</button>
//     <dialog data-dialog-target="dialog"
//             data-action="cancel->dialog#cancel close->dialog#closed click->dialog#clickOutside">
//       <button type="button" data-action="dialog#close">Close</button>
//       ...
//     </dialog>
//   </div>
//
// - `locked` value: while true, Escape, backdrop clicks, and close() calls are
//   refused. Toggle it from a composing controller via the
//   `data-dialog-locked-value` attribute.
// - Dispatches bubbling "dialog:opened" and "dialog:closed" events so composing
//   controllers can react without reaching into this controller.
// - Restores focus to the element that opened the dialog.
export default class extends Controller {
  static targets = [ "dialog" ]
  static values = { locked: Boolean }

  open() {
    if (this.dialogTarget.open) {
      return
    }

    this.opener = document.activeElement
    this.dialogTarget.showModal()
    this.focusInitialElement()
    this.emit("dialog:opened")
  }

  close() {
    if (this.lockedValue || !this.dialogTarget.open) {
      return
    }

    this.dialogTarget.close()
  }

  // The native "cancel" event fires on Escape; refuse it only while locked so
  // Escape keeps working whenever closing is safe.
  cancel(event) {
    if (this.lockedValue) {
      event.preventDefault()
    }
  }

  // A click on the <dialog> element itself (not its content) is a backdrop
  // click; treat it like Escape.
  clickOutside(event) {
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  // The native "close" event fires however the dialog closed; restore focus to
  // the opener and notify composing controllers.
  closed() {
    if (this.opener && typeof this.opener.focus === "function") {
      this.opener.focus()
    }
    this.opener = null
    this.emit("dialog:closed")
  }

  // Prefer an explicitly marked element; otherwise the native showModal focus
  // behavior (first focusable element) already applies.
  focusInitialElement() {
    const preferred = this.dialogTarget.querySelector("[data-dialog-initial-focus]")

    if (preferred) {
      preferred.focus()
    }
  }

  emit(name) {
    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true }))
  }
}
